// Dual-mount support: each sub-brand app is reachable both as a path under
// tickleapps.com/apps/<brand> and as its own subdomain (<brand>.tickleapps.com).
// Detected once at runtime from window.location.hostname — same build, same
// bundle, no build-time PUBLIC_URL/basename split required.

import routeMeta from '../data/routeMeta.json';

const SUBDOMAIN_BRANDS = {
  nutriscan: { hostname: 'nutriscan.tickleapps.com', pathPrefix: '/apps/nutriscan' },
  cardcycle: { hostname: 'cardcycle.tickleapps.com', pathPrefix: '/apps/cardcycle' },
};

// The one public origin that owns canonical identity. An app subdomain serves
// the same build, so its pages must point search engines and social cards at
// the matching tickleapps.com/apps/<brand> URL rather than at themselves.
// Read from data/routeMeta.json so the build-time shell generator and the
// browser cannot disagree about it (Release 2.0.1, task R02).
export const PARENT_SITE_ORIGIN = routeMeta.parentOrigin;

export function getActiveBrand() {
  if (typeof window === 'undefined') return null;
  const hostname = window.location.hostname;
  for (const [brand, cfg] of Object.entries(SUBDOMAIN_BRANDS)) {
    if (hostname === cfg.hostname) return brand;
  }
  return null;
}

// Builds the correct in-app path for a sub-brand route depending on the
// current mount: root-relative on the brand's own subdomain, prefixed with
// /apps/<brand> everywhere else (the main tickleapps.com domain).
export function brandPath(brand, subpath = '') {
  const activeBrand = getActiveBrand();
  if (activeBrand === brand) return subpath || '/';
  const cfg = SUBDOMAIN_BRANDS[brand];
  return `${cfg.pathPrefix}${subpath}`;
}

// Absolute URL on the parent site, for canonical identity and for leaving a
// subdomain. `path` must be root-relative ("/contact") or empty.
export function parentSiteUrl(path = '') {
  if (!path) return PARENT_SITE_ORIGIN;
  return `${PARENT_SITE_ORIGIN}${path.startsWith('/') ? path : `/${path}`}`;
}

// The parent-path route that owns a sub-brand page, regardless of which host
// served it: always /apps/<brand><subpath>.
export function appCanonicalPath(brand, subpath = '') {
  const cfg = SUBDOMAIN_BRANDS[brand];
  if (!cfg) throw new Error(`unknown brand: ${brand}`);
  return `${cfg.pathPrefix}${subpath}`;
}

// Canonical/Open Graph/social URL for a sub-brand page.
export function appCanonicalUrl(brand, subpath = '') {
  return parentSiteUrl(appCanonicalPath(brand, subpath));
}

// Destination for a parent-site route (Support, studio privacy) reached from an
// app page. On a sub-brand subdomain that route does not exist in the app route
// table, so it must be a full parent-site navigation; on the main domain the
// caller keeps using an in-app <Link>. Returns null there.
export function parentRouteHref(path) {
  return getActiveBrand() ? parentSiteUrl(path) : null;
}

// "Back to TickleApps" links: on a sub-brand subdomain, "/" is that brand's
// own home, not the main site — so it must be a full external navigation
// instead of an in-app <Link to="/">. Returns null on the main domain
// (use a normal internal Link there).
export function mainSiteHref() {
  return getActiveBrand() ? parentSiteUrl() : null;
}
